import { useState, useEffect, useCallback, useRef } from 'react';
import { obsAPI } from '../api';
import { gatePayload } from './noteGate';

// Long enough not to call the server on every keystroke, short enough that
// the save button comes back promptly once a note is fixed.
const RECHECK_MS = 400;

/**
 * The note gate, as the evidence form uses it. The rules are on the server;
 * this decides when to ask and what to show.
 *
 * A failure is shown once the observer has left the field it is about, or
 * has tried to save — not while they are still typing the first sentence,
 * and not as "Impact is required" before they reach the impact box. After a
 * field has been left, the note is re-checked as they type, so the save
 * button comes back the moment it is fixed.
 *
 * If the server cannot be reached, nothing is blocked here: the save itself
 * runs the gate, and nothing is written until it passes.
 */
export default function useNoteGate({ evidence, impact, who, identifier }) {
  const [touched, setTouched] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [result, setResult] = useState(null);

  // Only the newest answer counts: a slow reply about an older version of
  // the note must not overwrite a newer one.
  const latest = useRef(0);
  const fields = useRef({ evidence, impact, who, identifier });
  fields.current = { evidence, impact, who, identifier };

  const check = useCallback(async () => {
    const ticket = ++latest.current;
    try {
      const { data } = await obsAPI.checkNote(gatePayload(fields.current));
      if (ticket === latest.current) setResult(data);
    } catch {
      // Unreachable: leave the decision to the save.
    }
  }, []);

  // Once anything has been left or a save tried, keep the answer current.
  const engaged = attempted || Object.keys(touched).length > 0;
  useEffect(() => {
    if (!engaged) return undefined;
    const t = setTimeout(check, RECHECK_MS);
    return () => clearTimeout(t);
  }, [engaged, evidence, impact, who, identifier, check]);

  const gate = result && !result.pass && (attempted || touched[result.field]) ? result : null;

  /** Mark a field as left, and ask the server straight away. */
  const touch = useCallback(field => {
    setTouched(t => (t[field] ? t : { ...t, [field]: true }));
    check();
  }, [check]);

  /**
   * On save. Returns false only when the server has already refused this
   * exact note; otherwise the save goes ahead and the server decides.
   */
  const attempt = useCallback(() => {
    setAttempted(true);
    return !gate;
  }, [gate]);

  /** A save the server's gate refused: show its reason. */
  const refusedByServer = useCallback(g => {
    latest.current += 1;          // a check still in flight is now out of date
    setAttempted(true);
    setResult(g);
  }, []);

  /** After a save goes through, the next note starts clean. */
  const reset = useCallback(() => {
    latest.current += 1;
    setTouched({}); setAttempted(false); setResult(null);
  }, []);

  return {
    gate,                                   // the failure to show, or null
    blocked: Boolean(gate),                 // keeps the save button disabled
    errorFor: field => (gate && gate.field === field ? gate : null),
    touch,
    attempt,
    refusedByServer,
    reset,
  };
}
